import { useEffect } from 'react';
import { useSkinSession } from '../context/SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return '161, 224, 244';
  const n = parseInt(hex.replace(/^#/, ''), 16);
  if (Number.isNaN(n)) return '161, 224, 244';
  return [n >> 16, (n >> 8) & 255, n & 255].join(', ');
}

function normalizeGradientAngle(angle, fallback = 90) {
  const parsedAngle = Number(angle);
  if (!Number.isFinite(parsedAngle)) return fallback;
  return ((parsedAngle % 360) + 360) % 360;
}

function buildBackgroundGradient(primaryColor, secondaryColor, angle) {
  const normalizedAngle = normalizeGradientAngle(angle);
  const isVerticalHalfBlend = Math.abs(normalizedAngle) < 0.01 || Math.abs(normalizedAngle - 180) < 0.01;

  if (isVerticalHalfBlend) {
    return `linear-gradient(${normalizedAngle}deg, ${primaryColor} 0%, ${primaryColor} 30%, ${secondaryColor} 70%, ${secondaryColor} 100%)`;
  }

  return `linear-gradient(${normalizedAngle}deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;
}

/**
 * Applies highlight color from external_fields.highlight (page-setup).
 * Sets --skin-highlight and --skin-highlight-rgb for user info selection/highlight.
 */
function applyHighlightFromExternalFields(highlight) {
  if (!highlight || typeof highlight !== 'string') return;
  document.documentElement.style.setProperty('--skin-highlight', highlight);
  document.documentElement.style.setProperty('--skin-highlight-rgb', hexToRgb(highlight));
}

/**
 * Applies typography/font colors from external_fields.fonts (page-setup).
 * Sets --skin-text-primary, --skin-text-secondary, --skin-text-muted.
 */
function applyFontsFromExternalFields(fonts) {
  if (!fonts || typeof fonts !== 'object') return;
  const fallbackStack = "'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  const genericFamilies = new Set([
    'serif',
    'sans-serif',
    'monospace',
    'cursive',
    'fantasy',
    'system-ui',
    'ui-serif',
    'ui-sans-serif',
    'ui-monospace',
  ]);

  const cleanToken = (value) => String(value || '').trim().replace(/^['"]+|['"]+$/g, '');

  const quoteIfNeeded = (value) => {
    const cleaned = cleanToken(value);
    if (!cleaned) return '';
    return genericFamilies.has(cleaned.toLowerCase())
      ? cleaned
      : `'${cleaned.replace(/'/g, "\\'")}'`;
  };

  const pickFontFamilyValue = () => {
    const candidates = [
      fonts.family,
      fonts.font_family,
      fonts.fontFamily,
      fonts.primary_family,
      fonts.primaryFamily,
      fonts.name,
    ];
    const value = candidates.find((item) => typeof item === 'string' && item.trim());
    return value ? value.trim() : '';
  };

  const normalizeFontFamily = (fontFamily) => {
    if (!fontFamily) return '';

    const aliases = {
      poppins: 'PoppinsLocal',
      poppinslocal: 'PoppinsLocal',
      abeezee: 'ABeeZeeLocal',
      abeezeelocal: 'ABeeZeeLocal',
      alata: 'AlataLocal',
      alatalocal: 'AlataLocal',
      archivo: 'ArchivoLocal',
      archivolocal: 'ArchivoLocal',
      cinzel: 'CinzelLocal',
      cinzellocal: 'CinzelLocal',
      playfairdisplay: 'PlayfairDisplayLocal',
      'playfair display': 'PlayfairDisplayLocal',
      playfairdisplaylocal: 'PlayfairDisplayLocal',
      greatvibes: 'GreatVibesLocal',
      'great vibes': 'GreatVibesLocal',
      greatvibeslocal: 'GreatVibesLocal',
      raelagrotesque: 'RaelaGrotesqueLocal',
      'raela grotesque': 'RaelaGrotesqueLocal',
      raelagrotesquelocal: 'RaelaGrotesqueLocal',
      raela: 'RaelaGrotesqueLocal',
    };

    // Keep comma-separated custom stacks untouched and map each segment when recognized.
    if (fontFamily.includes(',')) {
      return fontFamily
        .split(',')
        .map((part) => {
          const raw = cleanToken(part);
          const key = raw.toLowerCase().replace(/[\s_-]+/g, ' ').trim();
          const compactKey = key.replace(/\s+/g, '');
          const mapped = aliases[key] || aliases[compactKey] || raw;
          return quoteIfNeeded(mapped);
        })
        .filter(Boolean)
        .join(', ');
    }

    const cleanedSingle = cleanToken(fontFamily);
    const key = cleanedSingle.toLowerCase().replace(/[\s_-]+/g, ' ').trim();
    const compactKey = key.replace(/\s+/g, '');
    return aliases[key] || aliases[compactKey] || cleanedSingle;
  };

  const fontFamily = pickFontFamilyValue();
  if (fontFamily) {
    const resolvedFontFamily = normalizeFontFamily(fontFamily);
    const fontStack = resolvedFontFamily.includes(',')
      ? `${resolvedFontFamily}, ${fallbackStack}`
      : `${quoteIfNeeded(resolvedFontFamily)}, ${fallbackStack}`;
    document.documentElement.style.setProperty('--skin-font-main', fontStack);
  }
  if (fonts.primary) {
    document.documentElement.style.setProperty('--skin-text-primary', fonts.primary);
  }
  if (fonts.secondary) {
    document.documentElement.style.setProperty('--skin-text-secondary', fonts.secondary);
  }
  if (fonts.muted) {
    document.documentElement.style.setProperty('--skin-text-muted', fonts.muted);
  }
}

/**
 * Applies CTA button CSS variables from external_fields.cta (page-setup).
 * Sets --cta-background (solid or linear-gradient with angle), --cta-text-color.
 */
function applyCtaFromExternalFields(cta) {
  if (!cta || typeof cta !== 'object') return;
  const primary = cta.primary_color;
  const secondary = cta.secondary_color;
  const backgroundType = (cta.background_type || 'solid').toLowerCase();
  const angle = typeof cta.gradient_angle === 'number' ? cta.gradient_angle : 90;
  const textColor = cta.text_color;

  if (textColor) {
    document.documentElement.style.setProperty('--cta-text-color', textColor);
  }
  if (backgroundType === 'gradient' && primary && secondary) {
    document.documentElement.style.setProperty(
      '--cta-background',
      `linear-gradient(${angle}deg, ${primary} 0%, ${secondary} 100%)`
    );
  } else if (primary || secondary) {
    document.documentElement.style.setProperty('--cta-background', primary || secondary);
  }
}

/**
 * Applies page-bg CSS variables from API external_fields.
 * Supports page-setup shape: external_fields.background { primary_color, secondary_color, type: "solid" | "gradient", gradient_angle }.
 * Also supports legacy shape: external_fields.primary_color / secondary_color.
 */
export function applyPageBgFromExternalFields(externalFields) {
  const bg = externalFields?.background;
  const primaryColor = bg?.primary_color ?? externalFields?.primary_color;
  const secondaryColor = bg?.secondary_color ?? externalFields?.secondary_color;
  const type = (bg?.type || '').toLowerCase();
  const gradientAngle = normalizeGradientAngle(bg?.gradient_angle, 90);
  const hasAnyColor = primaryColor || secondaryColor;
  if (!hasAnyColor) return;

  const useGradient = type === 'gradient' && primaryColor && secondaryColor;
  if (useGradient) {
    document.documentElement.style.setProperty('--page-bg-background', buildBackgroundGradient(primaryColor, secondaryColor, gradientAngle));
    document.documentElement.style.setProperty('--page-bg-primary', primaryColor);
    document.documentElement.style.setProperty('--page-bg-secondary', secondaryColor);
    document.documentElement.style.setProperty('--page-bg-primary-rgb', hexToRgb(primaryColor));
    document.documentElement.style.setProperty('--page-bg-secondary-rgb', hexToRgb(secondaryColor));
  } else {
    const solidColor = primaryColor || secondaryColor;
    document.documentElement.style.setProperty('--page-bg-background', solidColor);
    document.documentElement.style.setProperty('--page-bg-primary', solidColor);
    document.documentElement.style.setProperty('--page-bg-secondary', solidColor);
    document.documentElement.style.setProperty('--page-bg-primary-rgb', hexToRgb(solidColor));
    document.documentElement.style.setProperty('--page-bg-secondary-rgb', hexToRgb(solidColor));
  }
}

export const PAGE_SETUP_STORAGE_KEY = 'skin_page_setup_token';
export const PAGE_SETUP_DATA_PREFIX = 'skin_page_setup_data_';

export function getPageSetupDataKey(token) {
  return token ? PAGE_SETUP_DATA_PREFIX + String(token).trim() : null;
}

/** Persist page-setup result so it can be re-applied on refresh (e.g. from LandingPage after fetch). */
export function storePageSetupData(token, result) {
  const key = getPageSetupDataKey(token);
  if (!key || !result) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(result));
    sessionStorage.setItem(PAGE_SETUP_STORAGE_KEY, token);
  } catch (_) {}
}

/** Apply external_fields and top-level domain from page-setup response (theme, CTA, fonts, highlight). */
export function applyPageSetupResult(result) {
  if (!result) return;
  const externalFields = result?.external_fields ||
    result?.data?.external_fields ||
    (Array.isArray(result) && result[0]?.external_fields);
  if (externalFields) {
    applyPageBgFromExternalFields(externalFields);
    applyCtaFromExternalFields(externalFields?.cta);
    // Support both shapes:
    // 1) external_fields.fonts.{family,...}
    // 2) external_fields.{font_family,fontFamily,primary_family,...}
    applyFontsFromExternalFields(externalFields?.fonts || externalFields);
    applyHighlightFromExternalFields(externalFields?.highlight);
  }
  const domain = result?.domain ?? result?.data?.domain;
  if (domain && typeof domain === 'string') {
    document.documentElement.dataset.pageSetupDomain = domain;
  }
}

/**
 * When token is present, fetch page-setup and apply page-bg + CTA from external_fields.
 * On refresh, re-applies from sessionStorage so CTA and background persist (document styles are lost on reload).
 */
export function PageBackgroundSync() {
  const { token } = useSkinSession();

  useEffect(() => {
    if (!token) return;
    const dataKey = getPageSetupDataKey(token);
    try {
      const stored = dataKey ? sessionStorage.getItem(dataKey) : null;
      if (stored) {
        const parsed = JSON.parse(stored);
        applyPageSetupResult(parsed);
      }
    } catch (_) {}

    let cancelled = false;
    const url = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup`;
    fetchWithTimeout(url, { timeoutMs: 20000 })
      .then((res) => res.ok ? res.json() : null)
      .then((result) => {
        if (cancelled || !result) return;
        applyPageSetupResult(result);
        try {
          sessionStorage.setItem(PAGE_SETUP_STORAGE_KEY, token);
          if (dataKey) sessionStorage.setItem(dataKey, JSON.stringify(result));
        } catch (_) {}
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  return null;
}
